import { useState, useEffect, type FormEvent } from 'react';
import { Field } from '@/pages/Login';
import type { Baby, FeedType, Sex } from '@/types';
import { isGerman, getUnitSystem, gramsToLbOz, lbOzToGrams } from '@/i18n/locale';
import { Icon } from '@/components/ui/icons';

export interface BabyProfileFormProps {
  initial?: Baby;
  title?: string;
  onSubmit: (values: BabyFormValues) => Promise<void>;
  secondaryAction?: { label: string; onClick: () => void; danger?: boolean };
  submitLabel?: string;
}

export interface BabyFormValues {
  name: string;
  date_of_birth: string;
  sex?: Sex;
  birth_weight_grams?: number;
  feed_type: FeedType;
  conditions?: string;
  pediatrician_name?: string;
  pediatrician_phone?: string;
}

export function BabyProfileForm({
  initial,
  title,
  onSubmit,
  secondaryAction,
  submitLabel,
}: BabyProfileFormProps) {
  const de = isGerman();
  const defaultSubmit = submitLabel ?? (de ? 'Änderungen speichern' : 'Save changes');

  const [name, setName] = useState(initial?.name ?? '');
  const [dob, setDob] = useState(initial?.date_of_birth ?? '');
  const [sex, setSex] = useState<Sex | ''>(initial?.sex ?? '');
  // US browsers enter birth weight in lb + oz; stored value is always grams.
  const imperial = getUnitSystem() === 'imperial';
  const [birthWeight, setBirthWeight] = useState(
    initial?.birth_weight_grams ? String(initial.birth_weight_grams) : ''
  );
  const initialLbOz = initial?.birth_weight_grams ? gramsToLbOz(initial.birth_weight_grams) : null;
  const [bwLb, setBwLb] = useState(initialLbOz ? String(initialLbOz.lb) : '');
  const [bwOz, setBwOz] = useState(initialLbOz ? String(initialLbOz.oz) : '');
  const [feedType, setFeedType] = useState<FeedType>(initial?.feed_type ?? 'breastfed');
  const [conditions, setConditions] = useState(initial?.conditions ?? '');
  const [pedName, setPedName] = useState(initial?.pediatrician_name ?? '');
  const [pedPhone, setPedPhone] = useState(initial?.pediatrician_phone ?? '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setName(initial?.name ?? '');
    setDob(initial?.date_of_birth ?? '');
    setSex(initial?.sex ?? '');
    setBirthWeight(initial?.birth_weight_grams ? String(initial.birth_weight_grams) : '');
    const lbOz = initial?.birth_weight_grams ? gramsToLbOz(initial.birth_weight_grams) : null;
    setBwLb(lbOz ? String(lbOz.lb) : '');
    setBwOz(lbOz ? String(lbOz.oz) : '');
    setFeedType(initial?.feed_type ?? 'breastfed');
    setConditions(initial?.conditions ?? '');
    setPedName(initial?.pediatrician_name ?? '');
    setPedPhone(initial?.pediatrician_phone ?? '');
    setError(null);
  }, [initial?.id]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(de ? 'Bitte den Namen deines Babys eingeben.' : "Please add your baby's name.");
      return;
    }
    if (!dob) {
      setError(de ? 'Bitte das Geburtsdatum eingeben.' : 'Please add a date of birth.');
      return;
    }

    let bw: number | undefined;
    if (imperial && (bwLb.trim() || bwOz.trim())) {
      const lb = Number(bwLb || '0');
      const oz = Number(bwOz || '0');
      const grams = lbOzToGrams(lb, oz);
      if (!Number.isFinite(grams) || lb < 0 || oz < 0 || oz >= 16 || grams < 200 || grams > 7000) {
        setError('Birth weight should be between 0 lb 7 oz and 15 lb 6 oz (ounces 0–15).');
        return;
      }
      bw = grams;
    } else if (!imperial && birthWeight.trim()) {
      const parsed = Number(birthWeight);
      if (!Number.isFinite(parsed) || parsed < 200 || parsed > 7000) {
        setError(de
          ? 'Geburtsgewicht in Gramm (zwischen 200 und 7000).'
          : 'Birth weight should be in grams (between 200 and 7000).');
        return;
      }
      bw = Math.round(parsed);
    }

    setLoading(true);
    try {
      await onSubmit({
        name: name.trim(),
        date_of_birth: dob,
        sex: sex || undefined,
        birth_weight_grams: bw,
        feed_type: feedType,
        conditions: conditions.trim() || undefined,
        pediatrician_name: pedName.trim() || undefined,
        pediatrician_phone: pedPhone.trim() || undefined,
      });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1800);
    } catch (err) {
      setError((err as Error).message ?? (de ? 'Etwas ist schiefgelaufen.' : 'Something went wrong.'));
    } finally {
      setLoading(false);
    }
  };

  const todayISO = new Date().toISOString().slice(0, 10);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {title && <h2 className="font-display font-semibold text-slate">{title}</h2>}

      <Field
        label="Name"
        value={name}
        onChange={setName}
        required
        placeholder={de ? 'z. B. Lena' : 'e.g. Maya'}
      />

      <Field
        label={de ? 'Geburtsdatum' : 'Date of birth'}
        type="date"
        value={dob}
        onChange={setDob}
        required
        max={todayISO}
      />

      <div>
        <span className="text-sm text-slate font-medium mb-1.5 block" id="feed-type-label">
          {de ? 'Ernährung' : 'Feeding'}
        </span>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-labelledby="feed-type-label">
          {(['breastfed', 'formula', 'combo'] as FeedType[]).map((opt) => (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={feedType === opt}
              onClick={() => setFeedType(opt)}
              className="choice-segment"
            >
              {de
                ? opt === 'breastfed' ? 'Stillen' : opt === 'formula' ? 'Flasche' : 'Beides'
                : opt === 'breastfed' ? 'Breastfed' : opt === 'formula' ? 'Formula' : 'Combo'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-sm text-slate font-medium mb-1.5 block" id="sex-label">
          {de ? 'Geschlecht' : 'Sex'}{' '}
          <span className="text-slate-muted font-normal">(optional)</span>
        </span>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-labelledby="sex-label">
          {(
            [
              { v: '', label: de ? 'Überspringen' : 'Skip' },
              { v: 'female', label: de ? 'Weiblich' : 'Female' },
              { v: 'male', label: de ? 'Männlich' : 'Male' },
            ] as { v: Sex | ''; label: string }[]
          ).map((opt) => (
            <button
              key={opt.label}
              type="button"
              role="radio"
              aria-checked={sex === opt.v}
              onClick={() => setSex(opt.v)}
              className="choice-segment"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {imperial ? (
        <fieldset>
          <legend className="text-sm text-slate font-medium mb-1.5">
            Birth weight <span className="text-slate-muted font-normal">(optional)</span>
          </legend>
          <div className="grid grid-cols-2 gap-2">
            <label className="relative block">
              <span className="sr-only">Pounds</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={15}
                value={bwLb}
                onChange={(e) => setBwLb(e.target.value)}
                placeholder="7"
                className="input-field pr-10"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-muted">lb</span>
            </label>
            <label className="relative block">
              <span className="sr-only">Ounces</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={15}
                value={bwOz}
                onChange={(e) => setBwOz(e.target.value)}
                placeholder="8"
                className="input-field pr-10"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-muted">oz</span>
            </label>
          </div>
        </fieldset>
      ) : (
        <Field
          label={de ? 'Geburtsgewicht (Gramm, optional)' : 'Birth weight (grams, optional)'}
          type="number"
          inputMode="numeric"
          min={200}
          max={7000}
          step={10}
          value={birthWeight}
          onChange={setBirthWeight}
          placeholder={de ? 'z. B. 3200' : 'e.g. 3200'}
        />
      )}

      <label className="block">
        <span className="text-sm text-slate font-medium mb-1.5 block">
          {de ? 'Etwas, das ich wissen sollte?' : 'Anything I should know?'}{' '}
          <span className="text-slate-muted font-normal">(optional)</span>
        </span>
        <textarea
          value={conditions}
          onChange={(e) => setConditions(e.target.value)}
          rows={2}
          placeholder={de ? 'z. B. Frühchen, Reflux, Allergien' : 'e.g. preemie, reflux, allergies'}
          className="input-field resize-none"
        />
      </label>

      <Field
        label={de ? 'Name Kinderärztin/Kinderarzt (optional)' : 'Pediatrician name (optional)'}
        value={pedName}
        onChange={setPedName}
        placeholder={de ? 'z. B. Dr. Müller' : 'e.g. Dr. Patel'}
      />
      <Field
        label={de ? 'Telefon Kinderarztpraxis (optional)' : 'Pediatrician phone (optional)'}
        type="tel"
        value={pedPhone}
        onChange={setPedPhone}
        placeholder={de ? 'z. B. 044 123 45 67' : 'e.g. (555) 123-4567'}
      />

      {error && (
        <div className="rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700">
          {error}
        </div>
      )}

      {savedFlash && (
        <div
          className="flex items-center gap-1.5 rounded-lg bg-primary-50 border border-primary-100 px-3 py-2 text-sm text-primary-800"
          role="status"
        >
          <Icon.Check size={16} />
          {de ? 'Gespeichert' : 'Saved'}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={loading} className="btn-primary flex-1">
          {loading ? (de ? 'Speichert…' : 'Saving…') : defaultSubmit}
        </button>
        {secondaryAction && (
          <button
            type="button"
            onClick={secondaryAction.onClick}
            disabled={loading}
            className={
              'rounded-xl px-4 py-2.5 text-sm font-medium border transition-colors disabled:opacity-50 ' +
              (secondaryAction.danger
                ? 'border-danger/30 text-danger-700 hover:bg-danger/5'
                : 'border-primary-100 text-slate hover:bg-primary-50')
            }
          >
            {secondaryAction.label}
          </button>
        )}
      </div>
    </form>
  );
}
